﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// The page where the user enters an identifier and selects a flow to start a session.

// Can be tested for all customers.
// It's the first page displayed after logging in.

// There are a bunch of different use cases and features to consider:
// * An advisor interacts with the page to select subject and then create or resume a session.
// * External session id is provided as a query string parameter, the page will then try to resolve and
//   start the session. No user interaction is required.
// * Interaction id is specified as a query string paramter. A session cookie will be set to track
//   transactions for that interaction.
// * Subject identifier is specified as a query string parameter. The input value will be pre-filled.
// * Some users (customers) are restricted to start advisory sessions for them selves. Identifier
//   field will be pre-filled and grayed out.

// SKIP-ApplyThemeStyleCode.ps1

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import Grid from "~base/Components/Layout/Grid";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import classnames from "classnames";
import { useAdditionalStyles } from "./Styles";
import { reducer, ReducerContext, SessionState, StartPageState } from "./Store";
import { useSettings } from "~base/Settings/SettingsContext";
import Form from "~base/Components/Primitives/Form";
import SplashPage from "~base/Pages/Shared/SplashPage";
import SplashContent from "~base/Pages/Shared/SplashContent";
import { Button } from "~base/Components/Atoms/Button";
import { IntlContext, useIntl } from "~base/Internationalization/IntlContext";
import {
    getSpacingClasses,
    useJssSpacingStyles,
} from "~base/Components/Layout/JssSpacing";
import { horizontalCenterPadding } from "~base/Components/Primitives/TitleContainer";
import { LabelRadioButton } from "~base/Components/Primitives/LabelRadioButton";
import { TitleSelect } from "~base/Components/Primitives/TitleSelect";
import Expander from "~base/Components/Layout/Expander";
import Container from "~base/Components/Layout/Container";
import { useGetSessionStartInfo } from "~base/Services/API/Session/GetStartInfo";
import TitleIdentifier from "~base/Components/Primitives/TitleIdentifier";
import {
    IdentifierConfigurationInfo,
    IndividualIdentifierTypes,
    OrganizationIdentifierTypes,
} from "~base/Internationalization/ConfigurationInfo";
import * as sessionModel from "~base/Services/API/Session/Model";
import { tryFormat } from "~base/Types/Identifier";
import { useNavigateService } from "~base/Services/NavigateService";
import { useCreateSessionEnsureSubject } from "~base/Services/API/Session/CreateSession";
import { useEffect } from "react";
import {
    StartPageCustomizations,
    StartPageDefaultCustomization,
} from "./Customization";
import {
    getExpectedSubjectType,
    getIdentifierTypesForModels,
    getSessionModelForSubjectTypes,
    sortSessionItemsDescending,
} from "./Functions";
import { getErrorMessage } from "~base/Services/API";
import { PoweredByConsilio } from "~base/Components/Composite/PoweredByConsilio";
import UserInfoCard from "~base/Pages/Shared/UserInfoCard";
import { useGetSessions } from "~base/Services/API/Session/GetSessions";
import TextBlock from "~base/Components/Primitives/TextBlock";
import { useExtInteractionIdentifier } from "~base/Utilities/ExtIntractionIdentifier";
import { useSearchParams } from "react-router-dom";
import * as identifier from "~base/Types/Identifier";
import PromptOverlay from "~base/Components/Composite/PromptOverlay";
import {
    sessionNotResumableTextCode,
    useResumeSession,
} from "~base/Services/API/Session/ResumeSession";
import VersionInfo from "~base/Components/Composite/VersionInfo";
import { useServerContext } from "~base/Services/ServerContext";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type StartProps = {
    customizations?: StartPageCustomizations;
};

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const StartPage: React.FunctionComponent<StartProps> = (
    props: StartProps
) => {
    // Init hooks

    const customization = {
        ...StartPageDefaultCustomization,
        ...props.customizations,
    };
    const intl = useIntl();
    const classes = useAdditionalStyles(customization);
    const navigate = useNavigateService();
    const settings = useSettings();
    const [searchParams] = useSearchParams();
    const serverContext = useServerContext();
    const userLanguage = serverContext.user?.PreferredUiCulture;

    const reducerContext: ReducerContext = {
        intl: intl,
    };

    const contextWrappedReducer = (reducerContext: ReducerContext) => {
        return (state, action) => reducer(state, action, reducerContext);
    };

    // Init state

    const [state, dispatch, _] = useConsilioReducer(
        buildReducerId("StartPage"),
        contextWrappedReducer(reducerContext),
        {
            selectedSessionState: "New",
            subjectIdentifier: "",
            subjectIdentifierType: "",
            loading: true,
            enableLanguageSelect: true,
            matchingSubjectTypes: [],
        }
    );

    // Variables

    const serverVirtualPath =
        settings.valueAsString("serverVirtualPath") ?? "/";
    const allowEditProfile = settings.valueAsBool("Account.AllowEditProfile");
    const hideLogout = settings.valueAsBool("Account.HideLogout");
    const extLogoutUrl = settings.valueAsString("account.externalLogoutUrl");
    const logoutUrl = extLogoutUrl ?? `${serverVirtualPath}Account/Logout`;

    const isRoleSupport = state?.startInfo?.Role?.Value == "support";
    const isAdminRole =
        serverContext.user?.Roles?.some((r) =>
            ["assistant", "operator", "tenantadmin", "sysadmin"].includes(
                r.Name
            )
        ) ?? false;
    const defaultTenantSid = settings.valueAsString("defaultTenantSid");
    const selectedSessionModel = state.startInfo?.SessionModels?.find(
        (x) => x.SessionModelId == state.selectedSessionModelId
    );
    const [individualTypes, organizationTypes] = getIdentifierTypesForModels(
        state.startInfo?.AllowedSubjectIdentifierTypes
    );

    const idConfig = getIdConfig(
        intl,
        customization,
        individualTypes,
        organizationTypes
    );

    const formattedSubjectIdentifier =
        state.matchingSubjectTypes?.length == 1
            ? tryFormat(intl, state.subjectIdentifier, idConfig)
            : state.subjectIdentifier;

    const sessionModels = getSessionModelForSubjectTypes(
        state.matchingSubjectTypes,
        state.startInfo?.SessionModels
    );
    const sessionModelSelectOptions = sessionModels.map((x) => {
        return {
            value: x.SessionModelId.toString(),
            displayName: intl.formatText(x.SessionModelFqn),
        };
    });
    const selectedSessionModelHasMultipleLanguages =
        selectedSessionModel?.Locales?.length > 1;
    const selectedSessionModelLanguages = selectedSessionModel?.Locales?.map(
        (x) => {
            return { value: x.Id, displayName: x.DisplayName };
        }
    );

    const expectedSubjectType = getExpectedSubjectType(
        state.matchingSubjectTypes,
        sessionModels
    );

    const subjectIdentifierFqn = `${expectedSubjectType}|${formattedSubjectIdentifier}`;

    const userSessions =
        state.loadedSessionsForSubjectIdentifier ==
            formattedSubjectIdentifier && state.subjectSessions
            ? state.subjectSessions
            : new Array<sessionModel.Session>();

    const completeSessions = userSessions
        .filter((x) => x.state == "Completed")
        .sort(sortSessionItemsDescending)
        .map((session) => {
            return {
                value: session.id.toString(),
                displayName: customization.formatSessionDisplayName(
                    intl,
                    session,
                    sessionModels.find(
                        (sessionModel) =>
                            sessionModel.SessionModelId ===
                            session.sessionTemplateId
                    )
                ),
            };
        });
    const ongoingSessions = userSessions
        .filter((x) => x.state == "Active")
        .sort(sortSessionItemsDescending)
        .map((session) => {
            return {
                value: session.id.toString(),
                displayName: customization.formatSessionDisplayName(
                    intl,
                    session,
                    sessionModels.find(
                        (sessionModel) =>
                            sessionModel.SessionModelId ===
                            session.sessionTemplateId
                    )
                ),
            };
        });

    // Currently selected ongoing or completed session.
    const selectedSessionId = getSelectedSessionId(state);
    const selectedSession =
        state.subjectSessions &&
        state.subjectSessions.find((x) => x.id == selectedSessionId);

    // Sales opportunity
    const salesOpportunityFromQuery: string =
        searchParams.get("SalesOpportunity");

    // SubjectIdentifier
    const subjectIdentifierFromQuery = searchParams.get("SubjectIdentifier");

    // Session model to preselect on the form
    const sessionModelFqnFromQuery = searchParams.get("SessionModelFqn");

    // External interaction identifier
    const externalInteractionIdFromQuery = searchParams.get("ExtInteractionId");

    // control calling of hooks
    const callStartInfo =
        state.hasLoadedTextCodes &&
        state.startInfo == null &&
        state.errorMessage == null &&
        !state.navigateUri;
    const calLoadSessions =
        state.errorMessage == null &&
        formattedSubjectIdentifier !=
            state.loadedSessionsForSubjectIdentifier &&
        state.matchingSubjectTypes?.length > 0 &&
        state.startInfo.SessionModels.length > 0;

    // Reset back state for Firefox.
    useEffect(() => {
        // When navigating back in firefox the previous page is displayed from memory.
        // This causes the page to be in "loading" state, since we were navigating to a new page
        // when the page was last displayed. We dispatch an action to reset the state.

        window.addEventListener("pageshow", () => {
            dispatch({ type: "pageRefresh" });
        });
    }, [dispatch]);

    useEffect(() => {
        const tenantName = serverContext.user?.Tenant.Name;
        // iRad's SoPIntlContext remaps en-US → en-SE, but textcodes are loaded under
        // the original "en-US" locale. Check the loaded locale, not the formatting one.
        const localeForCheck =
            intl.culture === "en-SE" ? "en-US" : intl.cultureInfo.id;
        if (
            tenantName === "root" ||
            (!!tenantName &&
                intl.areTextCodesLoaded(localeForCheck, tenantName))
        ) {
            dispatch({ type: "setHasLoadedTextCodes", value: true });
        }
    }, [dispatch, intl, serverContext.user]);

    // API hooks

    const [loadingStartInfo, __] = useGetSessionStartInfo({
        call: callStartInfo,
        salesOpportunity: salesOpportunityFromQuery,
        subjectIdentifier: subjectIdentifierFromQuery,
        externalInteractionId: externalInteractionIdFromQuery,
        throwError: false,
        callback: async (status, response, responseData) => {
            if (status == "Success") {
                if (responseData.RequiresRegistration) {
                    const encodedRelativeUrl = getReturnUrlParam();
                    dispatch({
                        type: "navigate",
                        uri: `${serverVirtualPath}Account/Register?ReturnUrl=${encodedRelativeUrl}`,
                    });
                } else {
                    // Optionally get a subject identifier from QS params.
                    const [subjectIdentifier, subjectIdentifierTypesFromQs] =
                        getMatchingSubjectTypes(
                            intl,
                            customization,
                            subjectIdentifierFromQuery,
                            responseData
                        );

                    const requireSelection =
                        customization.requireSessionModelSelection?.(
                            responseData.SessionModels
                        ) ?? false;

                    dispatch({
                        type: "initDataLoaded",
                        data: responseData,
                        subjectIdentifierFromQs: subjectIdentifier,
                        sessionModelFqnFromQs: sessionModelFqnFromQuery,
                        subjectIdentifierTypes: subjectIdentifierTypesFromQs,
                        requireSessionModelSelection: requireSelection,
                    });

                    if (responseData.SessionModels.length == 0) {
                        dispatch({
                            type: "fetchError",
                            message: intl.formatText(
                                "VisiFinance.UI.Start.StartaRådgivningSaknarSessionsmodeller"
                            ),
                        });
                    }
                }
            } else {
                if (response.status == 401 || response.status == 403) {
                    if (isAdminRole) {
                        dispatch({
                            type: "navigate",
                            uri: `${serverVirtualPath}Admin`,
                            forceLoadAsExternal: true,
                        });
                    } else {
                        dispatch({
                            type: "navigate",
                            uri: `${serverVirtualPath}Account/AccessDenied?ReturnUrl=${getReturnUrlParam()}`,
                        });
                    }
                } else {
                    // At this point the loading spinner is active, so we throw an error and show the generic dialog instead.
                    throw new Error(await getErrorMessage(intl, response));
                }
            }
        },
    });

    const [loadingGetSessions, ___] = useGetSessions({
        call: calLoadSessions,
        subjectIdentifier: subjectIdentifierFqn,
        limit: 1000,
        salesOpportunity: salesOpportunityFromQuery,
        callback: async (status, response, responseData) => {
            if (status == "Success") {
                const resDataFiltered = {
                    ...responseData,
                    items: responseData.items.filter(
                        customization.filerSessionsResume
                    ),
                };
                dispatch({
                    type: "sessionsForSubjectLoaded",
                    subjectidentifier: formattedSubjectIdentifier,
                    data: resDataFiltered,
                });
            } else {
                dispatch({
                    type: "fetchError",
                    message: await getErrorMessage(intl, response),
                });
            }
        },
    });

    const [loadingCreateSession, createSessionEnsureSubjectApi] =
        useCreateSessionEnsureSubject({
            callback: async (status, errorResponse, response) => {
                if (status == "Success") {
                    const sessionGuideUrl = `${serverVirtualPath}Home/Main/${response.id}`;
                    dispatch({ type: "navigate", uri: sessionGuideUrl });
                } else {
                    dispatch({
                        type: "fetchError",
                        message: await getErrorMessage(intl, errorResponse),
                    });
                }
            },
        });

    const [loadinResumeSession, resumeSessionApi] = useResumeSession({
        sessionId: selectedSessionId,
        callback: async (status, errorResponse, response) => {
            if (status == "Success") {
                const sessionGuideUrl = `${serverVirtualPath}Home/Main/${response.sessionId}`;
                dispatch({ type: "navigate", uri: sessionGuideUrl });
            } else {
                dispatch({
                    type: "fetchError",
                    message: await getErrorMessage(
                        intl,
                        errorResponse,
                        sessionNotResumableTextCode
                    ),
                });
            }
        },
    });

    // Set external interaction identifier.
    useExtInteractionIdentifier(externalInteractionIdFromQuery);

    // Navigate to session based on external identifier provided in url.

    const extSessionIdParam = searchParams.get("ExtSessionId");

    useEffect(() => {
        // Dont trigger on back or forward navigation.
        if (
            window.performance.navigation.type != 2 &&
            state.subjectSessions != null &&
            extSessionIdParam != null
        ) {
            const loadSessionWithId = state.subjectSessions.find(
                (x) => x.externalIdentifier == extSessionIdParam
            )?.id;
            if (loadSessionWithId) {
                dispatch({
                    type: "navigate",
                    uri: `${serverVirtualPath}Home/Main/${loadSessionWithId}`,
                    replace: true,
                });
            }
        }
    }, [
        dispatch,
        state.loading,
        state.subjectSessions,
        extSessionIdParam,
        serverVirtualPath,
    ]);

    // Change URL if a navigation has been dispatched.

    useEffect(() => {
        if (state.navigateUri) {
            navigate.current(
                state.navigateUri,
                { replace: state.navigateReplace },
                state.navigateForceExternal
            );
        }
    }, [
        navigate,
        state.navigateUri,
        state.navigateReplace,
        state.navigateForceExternal,
    ]);

    // Rendering

    const portalRoot = document.getElementById("react-portal-root");

    const loadingRedirectToSession =
        loadingGetSessions && extSessionIdParam != null;

    const loadingTenantTextCodes =
        customization.waitForTextCodesToLoad && !state.hasLoadedTextCodes;

    const pageLoading =
        state.loading ||
        loadingStartInfo ||
        loadingCreateSession ||
        loadingRedirectToSession ||
        loadinResumeSession ||
        loadingTenantTextCodes;
    const formLoading = pageLoading || loadingGetSessions;

    const jssSpacingStyles = useJssSpacingStyles();
    const formVerticalSpacingClasses = getSpacingClasses(
        jssSpacingStyles,
        null,
        horizontalCenterPadding
    );

    const identifierLabelTextCode = getIdentifierLabelTextCode(
        customization,
        individualTypes,
        organizationTypes
    );

    const identifierReadOnly =
        state.startInfo?.RestrictToSubject === true ||
        (customization.makeSubjectIdReadOnlyIfFromQS &&
            state.subjectIdComesFromQS);

    // Language & locale.
    const preferredUiCulture =
        customization.tryForPreferredUiCulture ??
        serverContext.user?.PreferredUiCulture;
    const enableLanguageSelect = !(
        preferredUiCulture !== undefined && preferredUiCulture !== null
    );

    const displayLanguageSelectOption =
        state?.startInfo?.Role?.Value == "advisor" ||
        enableLanguageSelect ||
        customization.allowSelfServiceLanguageSelection;

    const chooseUserLocale =
        !displayLanguageSelectOption && customization.tryForPreferredUiCulture;
    const sessionLocale = chooseUserLocale
        ? userLanguage
        : state.selectedSessionModelLanguage;

    const formIsValid =
        state.matchingSubjectTypes.length > 0 &&
        !formLoading &&
        identifier.validate(intl, formattedSubjectIdentifier, idConfig)
            .isValid &&
        ((state.selectedSessionState == "New" &&
            selectedSessionModel != null &&
            ((!customization.allowMultipleCodes && !isRoleSupport) ||
                !!state.selectedRådgivarKod)) ||
            (state.selectedSessionState == "Ongoing" &&
                state.selectedOngoingSessionId != null) ||
            (state.selectedSessionState == "Completed" &&
                state.selectedCompletedSessionId != null)) &&
        !selectedSessionModel?.SessionModelRestrictedWithMessage;

    const headerContent = state.startInfo && (
        <UserInfoCard
            customization={customization.userInfoCustomization}
            className={classes.header}
            displayName={state.startInfo.UserDisplayName}
            role={state.startInfo.Role.TextCode}
            editLink={
                allowEditProfile &&
                !customization.hideRegistrationLink &&
                `${serverVirtualPath}Account/Register?ReturnUrl=${getReturnUrlParam()}`
            }
        />
    );

    const footerContent = (
        <Button
            testIdentifier="StartFooterButton"
            type="submit"
            className={classnames(formVerticalSpacingClasses, classes.button)}
            disabled={!formIsValid}
        >
            {intl.formatText(footerButtonTextCode(state.selectedSessionState))}
        </Button>
    );

    const initialInfRules: Array<sessionModel.InitialInfRuleRequest> = [
        {
            ident: "this.sessionStartInput", // Glii
            type: "VisiFinance.Consilio.Rådgivningssession.SessionStartInput", // the type of the member
            value: { associeradRådgivarKod: state.selectedRådgivarKod }, // the value to be assigned
        },
    ];

    return (
        <SplashPage isLoading={pageLoading} showSplashScreen={false}>
            <Form
                testIdentifier="StartForm"
                onSubmit={() => {
                    if (state.selectedSessionState == "New") {
                        let subjectType = "";
                        switch (state.subjectIdentifierType) {
                            case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson.Personnummer.SWE":
                                subjectType =
                                    "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson";
                                break;
                            case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson.Epost":
                                subjectType =
                                    "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson";
                                break;
                            case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.JuridiskPerson.Organisationsnummer.SWE":
                                subjectType =
                                    "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.JuridiskPerson";
                                break;
                            case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.JuridiskPerson.Organisationsnummer.NL":
                                subjectType =
                                    "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.JuridiskPerson";
                                break;
                            case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson.ExterntRegisterId":
                                subjectType =
                                    "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson";
                                break;
                        }

                        const createSessionData = {
                            locale: sessionLocale,
                            sessionTemplateId: state.selectedSessionModelId,
                            subjectIdentifier: formattedSubjectIdentifier,
                            subjectType: subjectType,
                            subjectIdentifierType: state.subjectIdentifierType,
                            externalSessionIdentifier: null,
                            initialInfRules: customization.allowMultipleCodes
                                ? initialInfRules
                                : null,
                            salesOpportunity: state.salesOpportunity,
                        };
                        if (state.errorMessage)
                            dispatch({ type: "fetchError", message: null });

                        createSessionEnsureSubjectApi(createSessionData);
                    } else if (
                        state.selectedSessionState == "Ongoing" ||
                        state.selectedSessionState == "Completed"
                    ) {
                        if (
                            selectedSession.access == "Read" &&
                            !state.acceptMessageSuppressed
                        ) {
                            dispatch({
                                type: "setAcceptMessage",
                                titleTextCode:
                                    "VisiFinance.UI.Start.ÅterupptaRådgivningSaknarSkrivrättigheter.Title",
                                messageTextCode:
                                    "VisiFinance.UI.Start.ÅterupptaRådgivningSaknarSkrivrättigheter.Meddelande",
                            });
                        } else {
                            if (state.selectedSessionState == "Completed") {
                                resumeSessionApi(
                                    state.selectedCompletedSessionId
                                );
                            } else if (
                                state.selectedSessionState == "Ongoing"
                            ) {
                                resumeSessionApi(
                                    state.selectedOngoingSessionId
                                );
                            }
                        }
                    }
                }}
                isValid={formIsValid}
            >
                <SplashContent
                    header={headerContent}
                    footer={footerContent}
                    constWidth={true}
                    closeRouteUri={hideLogout ? null : logoutUrl}
                    onCloseIconFQN={customization.onCloseIconFQN}
                >
                    <Grid templateColumns="">
                        <TitleIdentifier
                            testIdentifier={identifierLabelTextCode}
                            enabled={!formLoading && !identifierReadOnly}
                            titleTextCode={identifierLabelTextCode}
                            individualFormat={idConfig.individualFormat}
                            organizationFormat={idConfig.organizationFormat}
                            individualTypes={individualTypes}
                            organizationTypes={organizationTypes}
                            organizationSubTypes={idConfig.organizationSubTypes}
                            value={state.subjectIdentifier}
                            validateInitialValue={identifierReadOnly ?? false}
                            required={true}
                            setValue={(value, parsedValue) =>
                                dispatch({
                                    type: "setSubjectIdentifier",
                                    value: value,
                                    parsedValue: parsedValue,
                                })
                            }
                            selectValues={
                                state.startInfo?.PreviousSubjectIdentifiers
                            }
                            placeholder={customization.identifierPlaceholder}
                        />

                        <Expander
                            testIdentifier="StartSessionLifecycleExpander"
                            slideOut
                            expanded={state.matchingSubjectTypes.length > 0}
                        >
                            {state.startInfo &&
                                state.startInfo.SessionModels.length > 0 &&
                                !isRoleSupport && (
                                    <LabelRadioButton
                                        testIdentifier="VisiFinance.UI.Start.NyRådgivning"
                                        enabled={!formLoading}
                                        labelTextCode="VisiFinance.UI.Start.NyRådgivning"
                                        group="sessionLifecycleType"
                                        checked={
                                            state.selectedSessionState == "New"
                                        }
                                        labelClassName={classes.radioPadding}
                                        onChange={(x) => {
                                            if (x)
                                                dispatch({
                                                    type: "setSessionState",
                                                    state: "New",
                                                });
                                        }}
                                    />
                                )}

                            {ongoingSessions.length > 0 && (
                                <Container>
                                    <LabelRadioButton
                                        testIdentifier="VisiFinance.UI.Start.ÅterupptaRådgivning"
                                        labelTextCode="VisiFinance.UI.Start.ÅterupptaRådgivning"
                                        group="sessionLifecycleType"
                                        enabled={!formLoading}
                                        checked={
                                            state.selectedSessionState ==
                                            "Ongoing"
                                        }
                                        labelClassName={classes.radioPadding}
                                        onChange={(x) => {
                                            if (x)
                                                dispatch({
                                                    type: "setSessionState",
                                                    state: "Ongoing",
                                                });
                                        }}
                                    />
                                </Container>
                            )}

                            {completeSessions.length > 0 && (
                                <Container>
                                    <LabelRadioButton
                                        testIdentifier="VisiFinance.UI.Start.AvslutadeRådgivningar"
                                        labelTextCode="VisiFinance.UI.Start.AvslutadeRådgivningar"
                                        group="sessionLifecycleType"
                                        enabled={!formLoading}
                                        checked={
                                            state.selectedSessionState ==
                                            "Completed"
                                        }
                                        labelClassName={classes.radioPadding}
                                        onChange={(x) => {
                                            if (x)
                                                dispatch({
                                                    type: "setSessionState",
                                                    state: "Completed",
                                                });
                                        }}
                                    />
                                </Container>
                            )}

                            <Expander
                                testIdentifier="StartSessionStateExpander"
                                slideOut
                                expanded={state.selectedSessionState != null}
                            >
                                {state.selectedSessionState == "New" &&
                                    state.startInfo &&
                                    sessionModels?.length >
                                        (customization.hideSessionModelSelectWhenSingleChoice
                                            ? 1
                                            : 0) &&
                                    !isRoleSupport && (
                                        <TitleSelect
                                            testIdentifier="VisiFinance.UI.Start.Rådgivningsflöde"
                                            titleTextCode="VisiFinance.UI.Start.Rådgivningsflöde"
                                            enabled={!formLoading}
                                            required={true}
                                            value={state.selectedSessionModelId?.toString()}
                                            defaultLabelTextCode={
                                                state.requireSessionModelSelection
                                                    ? "VisiFinance.UI.Start.VäljFlöde"
                                                    : undefined
                                            }
                                            stringValues={
                                                sessionModelSelectOptions
                                            }
                                            onChange={(x) =>
                                                dispatch({
                                                    type: "selectSessionModel",
                                                    sessionModel:
                                                        sessionModels.find(
                                                            (y) =>
                                                                y.SessionModelId?.toString() ==
                                                                x
                                                        ),
                                                })
                                            }
                                        />
                                    )}

                                {state.selectedSessionState == "Ongoing" &&
                                    ongoingSessions.length > 0 && (
                                        <TitleSelect
                                            testIdentifier="VisiFinance.UI.Start.Rådgivning"
                                            enabled={!formLoading}
                                            titleTextCode="VisiFinance.UI.Start.Rådgivning"
                                            required={true}
                                            value={
                                                state.selectedOngoingSessionId +
                                                ""
                                            }
                                            stringValues={ongoingSessions}
                                            onChange={(x) =>
                                                dispatch({
                                                    type: "selectOngoingSession",
                                                    id: parseInt(x, 10),
                                                })
                                            }
                                        />
                                    )}

                                {state.selectedSessionState == "Completed" &&
                                    completeSessions.length > 0 && (
                                        <TitleSelect
                                            testIdentifier="VisiFinance.UI.Start.Rådgivning"
                                            enabled={!formLoading}
                                            titleTextCode="VisiFinance.UI.Start.Rådgivning"
                                            required={true}
                                            value={
                                                state.selectedCompletedSessionId +
                                                ""
                                            }
                                            stringValues={completeSessions}
                                            onChange={(x) => {
                                                dispatch({
                                                    type: "selectCompletedSession",
                                                    id: parseInt(x, 10),
                                                });

                                                //if (formattedSubjectIdentifier.startsWith("19930101-0105")) {
                                                //    throw "Test of error boundary during event.";
                                                //}
                                            }}
                                        />
                                    )}

                                <Expander
                                    testIdentifier="StartLanguageExpander"
                                    slideOut
                                    expanded={
                                        selectedSessionModelHasMultipleLanguages &&
                                        state.selectedSessionState == "New" &&
                                        !isRoleSupport &&
                                        displayLanguageSelectOption
                                    }
                                >
                                    <TitleSelect
                                        testIdentifier="VisiFinance.UI.Start.Språk"
                                        enabled={!formLoading}
                                        titleTextCode="VisiFinance.UI.Start.Språk"
                                        required={true}
                                        value={
                                            state.selectedSessionModelLanguage
                                        }
                                        stringValues={
                                            selectedSessionModelLanguages
                                        }
                                        onChange={(x) =>
                                            dispatch({
                                                type: "selectSessionModelLanguage",
                                                id: x,
                                            })
                                        }
                                    />
                                </Expander>

                                <Expander
                                    testIdentifier="StartRådgivarKodExpander"
                                    slideOut
                                    expanded={
                                        customization.allowMultipleCodes &&
                                        state.availableRådgivarKoder?.length >=
                                            2 &&
                                        state.selectedSessionState == "New"
                                    }
                                >
                                    <TitleSelect
                                        testIdentifier="VisiFinance.UI.Start.Rådgivarkod"
                                        enabled={!formLoading}
                                        titleTextCode="VisiFinance.UI.Start.Rådgivarkod"
                                        required={true}
                                        value={state.selectedRådgivarKod}
                                        stringValues={
                                            state.availableRådgivarKoder
                                        }
                                        onChange={(x) =>
                                            dispatch({
                                                type: "selectRådgivarKod",
                                                kod: x,
                                            })
                                        }
                                    />
                                </Expander>
                            </Expander>
                        </Expander>

                        {state.errorMessage && (
                            <Container className={classes.errorText}>
                                <TextBlock
                                    styleType="Paragraph"
                                    text={state.errorMessage}
                                    styleSubType="Validation"
                                    size="Small"
                                />
                            </Container>
                        )}
                    </Grid>
                </SplashContent>

                <PromptOverlay
                    testIdentifier="StartAcceptMessagePromptOverlay"
                    open={state.acceptMessageBodyTextCode != null}
                    portalContainer={portalRoot}
                    bodyTextCode={state.acceptMessageBodyTextCode}
                    titleTextCode={state.acceptMessageTitleTextCode}
                    saveButtonTextCode={"VisiFinance.UI.Start.Acceptera"}
                    showSave={true}
                    onSave={() => {
                        dispatch({
                            type: "clearAcceptMessage",
                            suppressed: true,
                        });

                        if (state.selectedSessionState == "Ongoing") {
                            resumeSessionApi(state.selectedOngoingSessionId);
                        } else if (state.selectedSessionState == "Completed") {
                            resumeSessionApi(state.selectedCompletedSessionId);
                        }
                    }}
                    onCancel={() => dispatch({ type: "clearAcceptMessage" })}
                    onClose={() => dispatch({ type: "clearAcceptMessage" })}
                />
            </Form>
            <div className={classes.bottom}>
                <PoweredByConsilio
                    testIdentifier="PoweredByConsilio"
                    hideVersionInfo={customization.versionInfo != "PoweredBy"}
                />
                {customization.adminMenu &&
                    state.startInfo &&
                    customization.adminMenu(state.startInfo.Role.Value)}
                {customization.versionInfo == "VersionInfoControl" && (
                    <VersionInfo testIdentifier="StartVersionInfo" />
                )}
            </div>
        </SplashPage>
    );
};

const footerButtonTextCode = (selectedSessionState: SessionState) => {
    if (selectedSessionState == "Ongoing")
        return "VisiFinance.UI.Start.ÅterupptaRådgivning";

    if (selectedSessionState == "Completed")
        return "VisiFinance.UI.Start.AvslutadRådgivning";

    return "VisiFinance.UI.Start.StartaRådgivning";
};

const getMatchingSubjectTypes = (
    intl: IntlContext,
    customization: StartPageCustomizations,
    subjectIdentifierParam: string,
    startInfo: sessionModel.GetStartInfoResponse
): [string, identifier.IdentifierParseTypes] => {
    if (!subjectIdentifierParam) {
        return [null, null];
    }

    const [individualTypes, organizationTypes] = getIdentifierTypesForModels(
        startInfo?.AllowedSubjectIdentifierTypes
    );
    const idConfig = getIdConfig(
        intl,
        customization,
        individualTypes,
        organizationTypes
    );
    const builtInResult = identifier.validate(
        intl,
        subjectIdentifierParam,
        idConfig
    );

    const formattedSubjectIdentifier =
        builtInResult.parsedValue.matchingTypes?.length == 1
            ? tryFormat(intl, subjectIdentifierParam, idConfig)
            : subjectIdentifierParam;

    return [formattedSubjectIdentifier, builtInResult.parsedValue];
};

const getReturnUrlParam = () => {
    const relativeUrl =
        window.location.pathname +
        window.location.search +
        window.location.hash;
    const encodedRelativeUrl = encodeURIComponent(relativeUrl);
    return encodedRelativeUrl;
};

function getIdentifierLabelTextCode(
    customization: StartPageCustomizations,
    individualTypes: IndividualIdentifierTypes[],
    organizationTypes: OrganizationIdentifierTypes[]
) {
    if (customization.identifierTextCode) {
        return customization.identifierTextCode;
    }

    if (individualTypes.length > 0 && organizationTypes.length > 0)
        return "VisiFinance.UI.Start.PersonEllerOrgNr";

    if (organizationTypes.length > 0) return "VisiFinance.UI.Start.OrgNr";

    return "VisiFinance.UI.Start.PersonNr";
}

function getSelectedSessionId(state: StartPageState) {
    if (state.selectedSessionState == "Ongoing")
        return state.selectedOngoingSessionId;

    if (state.selectedSessionState == "Completed")
        return state.selectedCompletedSessionId;

    return null;
}

const getIdConfig = (
    intl: IntlContext,
    customization: StartPageCustomizations,
    individualIdTypes: IndividualIdentifierTypes[],
    orgIdTypes: OrganizationIdentifierTypes[]
): IdentifierConfigurationInfo => {
    return {
        ...intl.configInfo.identifier,
        individualFormat:
            customization.individualIdentifierFormat ??
            intl.configInfo.identifier.individualFormat,
        organizationFormat:
            customization.organizationIdentifierFormat ??
            intl.configInfo.identifier.organizationFormat,
        individualTypes: individualIdTypes,
        organizationTypes: orgIdTypes,
        organizationSubTypes: [
            ...intl.configInfo.identifier.organizationSubTypes,
            ...customization.additionalAllowedOrganizationTypes,
        ],
    };
};
